/**
 * The approval workflow.
 *
 * Everything here goes through callables. Approval state is not something a
 * client may write: a decision advances an instance to the next step, may
 * complete it, and writes an audit entry — and the whole point of an approval
 * record is that nobody could have forged it.
 *
 * The lifecycle the owner described: create, submit, approved or rejected,
 * repeat. Each organization decides how many steps and which role holds each
 * one. Role, not department — "more of a person with a certain
 * responsibility", which is how a hospital actually assigns sign-off.
 */

import { formatDay } from '../../shared/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import type { Role } from '../../types/roles';

export type DecisionType = 'approved' | 'rejected' | 'requestChanges';

export interface ApprovalStep {
  stepId: string;
  workflowId: string;
  stepOrder: number;
  name: string;
  description?: string;
  /** The role that holds this step. */
  roleId?: string;
  requiredApprovals?: number;
  allowParallelApprovals?: boolean;
  isActive?: boolean;
  /** Named people who hold the step. When any are named, the role no longer decides who may act. */
  assigneeUids?: string[];
  /** Only people in the document's own department hold it. */
  departmentOnly?: boolean;
  /** A committee's step: approving it records the meeting date, the vote and the minutes reference. */
  isCommittee?: boolean;
  /** Runs at the same time as the step above it. */
  parallelWithPrevious?: boolean;
  /** Days the step is given; null or absent means no deadline. */
  dueDays?: number | null;
  /** On a waiting step: when it is due. ISO date-time. */
  dueAt?: string | null;
}

/** What a step can be set to beyond its name and role. */
export interface StepSettingsInput {
  assigneeUids?: string[];
  departmentOnly?: boolean;
  isCommittee?: boolean;
  parallelWithPrevious?: boolean;
  dueDays?: number | null;
  requiredApprovals?: number;
}

export interface ApprovalWorkflow {
  workflowId: string;
  name: string;
  /** One line, shown in the list. */
  description?: string;
  /** In detail, shown only on the workflow's own page. */
  longDescription?: string;
  artifactType: string;
  isDefault?: boolean;
  isActive?: boolean;
  /** The submitter may not decide, and one person may not decide two steps. Absent means yes. */
  separationOfDuties?: boolean;
  /** Hold an approved document until its effective date. */
  holdUntilEffective?: boolean;
  /** Roles told when something happens, who decide nothing. */
  notifyRoles?: NotifyRoles;
  /**
   * Whether an author may pick it at submit, and only then for a document that
   * would otherwise use the catch-all: a specific route is never replaced by an
   * author. Administrators override regardless. Absent means yes.
   */
  authorsMayChoose?: boolean;
  /** Active steps, as the list callable counts them. */
  stepCount?: number;
  /**
   * How many versions it has been. Written by the server on every change and
   * already on the record the list callable returns; declared here so the page
   * can show it without a second read.
   */
  versionCount?: number;
  /** The steps themselves, in order. */
  steps?: { order: number; name: string; roleId: string | null }[];
  /** The document types and departments it is for. Empty on both: it names nothing. */
  covers?: { documentTypeIds?: string[]; departmentIds?: string[] };
}

export interface NotifyRoles { submitted?: string[]; published?: string[]; returned?: string[] }

export type MatchLevel = 'type_and_department' | 'type' | 'department' | 'catch_all' | 'chosen';

/** What `previewApprovalRouteFn` answers: the chain, and why that one. */
export interface RoutePreview {
  route: {
    workflowId: string;
    workflowName: string;
    level: MatchLevel;
    reason: string;
    documentTypeName: string | null;
    departmentName: string | null;
    skipped: { workflowId: string; workflowName: string; level: MatchLevel }[];
  } | null;
  steps: { order: number; name: string; roleId: string | null; holders: number | null; from: string | null; named?: boolean; departmentOnly?: boolean; sameTimeAsPrevious?: boolean }[];
  /** What routing gives this document, whatever is being shown. */
  defaultRoute?: { workflowId: string; workflowName: string; reason: string } | null;
  /** The other workflows this person may pick instead, each with its route. */
  choices?: { workflowId: string; name: string; description: string | null; steps: { order: number; name: string; roleId: string | null }[] }[];
}

export interface ApprovalInstance {
  instanceId: string;
  artifactId: string;
  artifactType: string;
  workflowId: string;
  currentStepOrder: number;
  status: string;
  startedAt?: unknown;
  /** Snapshotted at submission, so the list says what each document is. */
  artifactTitle?: string;
  artifactNumber?: string | null;
  artifactVersionNumber?: number | null;
  changeSummary?: string | null;
  submittedByName?: string | null;
  submittedByEmail?: string | null;
  /** Why this chain. `chosen` is set when the author picked a workflow in place of the default. */
  routing?: { workflowName?: string; level?: MatchLevel; reason?: string; chosen?: { defaultWorkflowName: string | null; byName: string | null; reason: string } };
}

/** What a decision did to the document, beyond recording itself. */
export interface DecisionResult {
  decisionId: string;
  instanceStatus: string;
  advanced: boolean;
  published?: { versionNumber: number; versionId: string };
  returned?: boolean;
  nextStep?: { name: string; stepOrder: number };
  /** Approved, but the document waits for its effective date. ISO date-time. */
  heldUntil?: string;
}

/** The sentence an approver reads after deciding. */
export function decisionOutcome(result: DecisionResult | undefined): string {
  if (result?.published) return `Approved. Published as version ${result.published.versionNumber}; staff can read it now.`;
  // Approved, and the last approval — but held for a date staff have not reached.
  if (result?.heldUntil) {
    /**
     * The effective date is a DAY, and days are read in UTC.
     *
     * This rendered it with the reader's own time zone, so an approver in New
     * York was told a policy effective on 1 January would publish on 31
     * December. Every other date on screen goes through `formatDay`, which
     * reads the calendar date the value names; this was the one that did not.
     */
    const on = formatDay(result.heldUntil) || 'its effective date';
    return `Approved. It will be published on ${on}; staff do not see it until then.`;
  }
  if (result?.returned) return 'Returned to the author with your comments.';
  if (result?.nextStep) return `Recorded. It now waits on ${result.nextStep.name}.`;
  return 'Decision recorded.';
}

export interface PendingApprovals {
  instances: ApprovalInstance[];
  /** The step each instance is currently sitting on, keyed by instance. */
  currentSteps: Record<string, ApprovalStep> | ApprovalStep[];
  total: number;
}

/** What this person has been asked to approve. */
export function usePendingApprovals(enabled: boolean) {
  return useQuery({
    queryKey: ['pending-approvals'],
    enabled,
    queryFn: () => call<Record<string, never>, PendingApprovals>('getPendingApprovalsFn', {}),
  });
}

export function useApprovalWorkflows(enabled: boolean) {
  return useQuery({
    queryKey: ['approval-workflows'],
    enabled,
    queryFn: () =>
      call<{ includeInactive: boolean }, { workflows: ApprovalWorkflow[] }>(
        'listApprovalWorkflowsFn',
        { includeInactive: true },
      ),
  });
}

export function useApprovalSteps(workflowId: string | null) {
  return useQuery({
    queryKey: ['approval-steps', workflowId],
    enabled: Boolean(workflowId),
    queryFn: () =>
      call<{ workflowId: string }, { steps: ApprovalStep[] }>('listApprovalStepsFn', {
        workflowId: workflowId!,
      }),
  });
}

/** Record approve, reject, or request changes against a step. */
export function useRecordDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      instanceId: string;
      stepId: string;
      decisionType: DecisionType;
      comments?: string;
      /** For a committee's step: when it met, how it voted, where the minutes are. */
      committee?: { meetingDate: string; vote?: string; minutesRef?: string };
    }) => call<typeof input, DecisionResult>('recordApprovalDecisionFn', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      void queryClient.invalidateQueries({ queryKey: ['policy'] });
      void queryClient.invalidateQueries({ queryKey: ['policy-counts'] });
    },
  });
}

function useWorkflowMutation<TInput>(name: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => call<TInput, unknown>(name, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-workflows'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-steps'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-workflow-versions'] });
    },
  });
}

export function useCreateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string; longDescription?: string; artifactType: string; isDefault?: boolean }) =>
      call<typeof input, { workflowId: string }>('createApprovalWorkflowFn', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-workflows'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-steps'] });
    },
  });
}

/** A workflow and its whole chain in one call; the server writes them together or not at all. */
export function useCreateWorkflowWithSteps() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      exampleKey?: string;
      makeDefault: boolean;
      documentTypeIds?: string[];
      departmentIds?: string[];
      /** `isCommittee` is the one setting a ready-made step carries. See exampleChains.ts. */
      steps: { name: string; roleId: Role; isCommittee?: boolean }[];
    }) => call<typeof input, { workflowId: string; steps: number; coverageRefused?: string | null }>('createApprovalWorkflowWithStepsFn', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-workflows'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-steps'] });
    },
  });
}

/** One version a workflow has been: the whole workflow as it then stood, who changed it, when, and what they did. */
export interface WorkflowVersion {
  version: number;
  change: string;
  by: { uid: string | null; name: string | null; email: string | null };
  /** ISO date-time. */
  at: string | null;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  authorsMayChoose: boolean;
  covers: { documentTypeIds: string[]; departmentIds: string[] };
  steps: { stepId: string; order: number; name: string; roleId: string | null; requiredApprovals: number }[];
}

/** Every version of a workflow, newest first. Asked for only when somebody opens the history. */
export function useWorkflowVersions(workflowId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['approval-workflow-versions', workflowId],
    enabled: enabled && Boolean(workflowId),
    staleTime: 0,
    queryFn: () => call<{ workflowId: string }, { versions: WorkflowVersion[] }>('listApprovalWorkflowVersionsFn', { workflowId: workflowId! }),
  });
}

/** Say which document types and departments a workflow is for. The server refuses a tie with another workflow. */
export function useSetCoverage() {
  return useWorkflowMutation<{ workflowId: string; documentTypeIds: string[]; departmentIds: string[] }>('setApprovalWorkflowCoverageFn');
}

/**
 * The chain a document would follow, from the resolver the submission uses.
 * For a policy as it stands, or for a type and department ("what would happen if").
 */
export function useRoutePreview(
  ask: { policyId: string; workflowId?: string } | { documentTypeId: string | null; departmentId: string | null } | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['approval-route', ask],
    enabled: enabled && ask !== null,
    staleTime: 0, // a workflow edited a moment ago must show
    retry: false,
    queryFn: () => call<NonNullable<typeof ask>, RoutePreview>('previewApprovalRouteFn', ask!),
  });
}

/** Rename a workflow, make it the one for everything else, or retire it. */
export function useUpdateWorkflow() {
  return useWorkflowMutation<{ workflowId: string; name?: string; description?: string; longDescription?: string; isDefault?: boolean; isActive?: boolean; authorsMayChoose?: boolean; separationOfDuties?: boolean; holdUntilEffective?: boolean; notifyRoles?: NotifyRoles }>('updateApprovalWorkflowFn');
}

/** A ready-made set of steps, added to a workflow in one write. */
export function useAddSteps() {
  return useWorkflowMutation<{ workflowId: string; setKey?: string; steps: { name: string; roleId: Role; isCommittee?: boolean }[] }>('addApprovalStepsFn');
}

/** Move steps up and down: the whole order, set at once on the server. */
export function useReorderSteps() {
  return useWorkflowMutation<{ workflowId: string; stepIds: string[] }>('reorderApprovalStepsFn');
}

export function useCreateStep() {
  return useWorkflowMutation<{
    workflowId: string;
    stepOrder: number;
    name: string;
    roleId?: Role;
    requiredApprovals?: number;
  }>('createApprovalStepFn');
}

export function useUpdateStep() {
  return useWorkflowMutation<{
    stepId: string;
    stepOrder?: number;
    name?: string;
    roleId?: Role;
  } & StepSettingsInput>('updateApprovalStepFn');
}

export function useDeleteStep() {
  return useWorkflowMutation<{ stepId: string }>('deleteApprovalStepFn');
}

/**
 * Normalize the current-step lookup.
 *
 * `getPendingApprovalsFn` returns `currentSteps` alongside `instances`, and
 * the service is free to hand back either a map keyed by instance or a
 * parallel array. Both are handled rather than assumed, because guessing
 * wrong would show every approval sitting on the same step.
 */
export function stepForInstance(
  pending: PendingApprovals | undefined,
  instance: ApprovalInstance,
  index: number,
): ApprovalStep | null {
  if (!pending) return null;
  const steps = pending.currentSteps;

  if (Array.isArray(steps)) return steps[index] ?? null;
  return steps?.[instance.instanceId] ?? null;
}

/** Steps in the order they are worked, lowest first. */
export function sortSteps(steps: readonly ApprovalStep[]): ApprovalStep[] {
  return [...steps].sort((a, b) => a.stepOrder - b.stepOrder);
}
